import { type Transaction } from "prosemirror-state";
import { useCallback } from "react";

import { schema } from "@moment/prosemirror-schema";
import { createCellID } from "@moment/prosemirror-utils";
import { useDocumentFromCurrentRoute, usePageFromCurrentRoute } from "@moment/state";

import { type DndComponentTemplate } from "~/components/common/dnd";
import { type ComputeCellNodeAttrs } from "~/models/cell";

import { type ComponentTemplateItem } from "../properties-pane/components-tab/types";
import { useComputeCells } from "./useComputeCells";

/**
 * A utility hook that allows for inserting a template into the editor at a given position. This is
 * useful both in the drag-and-drop handlers, and in the "click-to-add" Recommendator templates.
 */
export const useEaselInsertTemplateAtPos = () => {
	const { documentId } = useDocumentFromCurrentRoute();
	const { pageId } = usePageFromCurrentRoute();
	const { makeUniqueCellName } = useComputeCells(documentId, pageId);

	return useCallback(
		(tr: Transaction, template: ComponentTemplateItem | DndComponentTemplate, pos: number) => {
			//
			// Insert the cells from the template.
			//

			const cellNameRefs: string[] = [];
			const cellTemplates = Array.isArray(template.code) ? template.code : [template.code];
			for (const cellTemplate of cellTemplates) {
				const cellName = makeUniqueCellName(template.cellNameTemplate, cellNameRefs);
				cellNameRefs.push(cellName);

				const nodeAttrs: ComputeCellNodeAttrs = { id: createCellID(), cellName };
				const computeCell = schema.nodes.compute_cell.create(
					nodeAttrs,
					schema.text(
						cellTemplate({
							id: createCellID(),
							refs: cellNameRefs,
						})
					)
				);

				tr = tr.insert(pos, computeCell);
			}

			//
			// ProseMirror will often add an empty paragraph automatically because of how
			// the schema is constructed. Let's delete it.
			//

			const sel = tr.selection;
			const selectedNodeFrom = sel.$from.node();
			if (
				selectedNodeFrom.type === schema.nodes.paragraph &&
				selectedNodeFrom.content.size === 0
			) {
				tr = tr.delete(sel.from - 1, sel.to + 1);
			}

			return tr;
		},
		[makeUniqueCellName]
	);
};
